'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

const TOKEN_KEY = 'gofusion_token'
const REDIRECT_URL_KEY = 'gofusion_redirect_url'
const API_URL_KEY = 'gofusion_api_url'

const DEFAULT_REDIRECT_URL = 'https://web-app.gofusion.fr'
const DEFAULT_API_URL = 'https://backoffice.gofusion.fr/api/v1'

export const useGoFusionToken = () => {
  const searchParams = useSearchParams()
  const [token, setToken] = useState<string | null>(null)
  const [redirectUrl, setRedirectUrl] = useState<string>(DEFAULT_REDIRECT_URL)
  const [apiUrl, setApiUrl] = useState<string>(DEFAULT_API_URL)
  const [hasTakenTest, setHasTakenTest] = useState<boolean | null>(null)

  useEffect(() => {
    const urlToken = searchParams.get('token')
    const urlRedirectUrl = searchParams.get('redirect_url')
    const urlApiUrl = searchParams.get('api_url')
    const urlHasTakenTest = searchParams.get('hasTakenTest')

    if (urlToken) {
      const previousToken = sessionStorage.getItem(TOKEN_KEY)
      if (previousToken && previousToken !== urlToken) {
        // New token means a different user — purge stale webhook-sent keys from the previous session
        const keysToRemove: string[] = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key?.startsWith(`gofusion_webhook_sent_${previousToken}`)) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key))

        // hasTakenTest=false confirms this new user hasn't completed the test yet.
        // Clear the localStorage simulation state so the inherited progression from
        // the previous user doesn't let this user bypass the fin-page guard.
        if (urlHasTakenTest === 'false') {
          localStorage.removeItem('nosgestesclimat::v3')
        }
      }
      sessionStorage.setItem(TOKEN_KEY, urlToken)
      setToken(urlToken)
    } else {
      const storedToken = sessionStorage.getItem(TOKEN_KEY)
      if (storedToken) setToken(storedToken)
    }

    if (urlRedirectUrl) {
      sessionStorage.setItem(REDIRECT_URL_KEY, urlRedirectUrl)
      setRedirectUrl(urlRedirectUrl)
    } else {
      const stored = sessionStorage.getItem(REDIRECT_URL_KEY)
      if (stored) setRedirectUrl(stored)
    }

    if (urlApiUrl) {
      sessionStorage.setItem(API_URL_KEY, urlApiUrl)
      setApiUrl(urlApiUrl)
    } else {
      const stored = sessionStorage.getItem(API_URL_KEY)
      if (stored) setApiUrl(stored)
    }

    if (urlHasTakenTest !== null) {
      setHasTakenTest(urlHasTakenTest === 'true')
    }
  }, [searchParams])

  return { token, redirectUrl, apiUrl, hasTakenTest }
}
